import { LoaderCircle, X } from 'lucide-react'

import ImageViewer from '@/buyers/components/ImageViewer'
import Upload from '@/buyers/pages/CreateExternalOrder/Upload'
import Action from '@/gf/components/Action'

/**
 * The three states of a document upload that feeds extraction, in order: the drop
 * zone, the processing spinner, then the filename chip beside a preview of the file.
 *
 * Log an Order, Order Manually and Enter Quote all run the same round trip, so they
 * share this rather than each rebuilding it — a buyer who has used one recognises
 * the others (GEA-6418). The caller supplies the surrounding card.
 */
const DocumentUpload = ({
  title,
  uploadContext,
  processing,
  processingDescription,
  documentUrl,
  // `previewUrl` is the authed URL to render the file with before the form is
  // submitted (GEA-4585). The server always sends one; the `documentUrl` fallback
  // below answers 403 now the bucket is private.
  documentPreviewUrl,
  onStarted,
  onComplete,
  onClear,
}: {
  title: string
  uploadContext: string
  processing: boolean
  processingDescription: string
  documentUrl: string | null
  documentPreviewUrl: string | null
  onStarted: () => void
  onComplete: (url: string, previewUrl: string | null) => void
  onClear: () => void
}) => {
  if (processing)
    return (
      <>
        <div className="text-lg font-medium">Processing Document</div>
        <div className="border border-gray-200 rounded-lg p-8 flex flex-col items-center justify-center space-y-4">
          <LoaderCircle className="animate-spin h-8 w-8 text-blue-500" />
          <div className="text-center space-y-2">
            <p className="font-medium">Processing file</p>
            <p className="text-sm text-gray-600">{processingDescription}</p>
          </div>
          <Action.S onClick={onClear} className="mt-4">
            Cancel
          </Action.S>
        </div>
      </>
    )

  if (documentUrl)
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between bg-gray-100 px-2 py-1 rounded-md max-w-full gap-2 border border-gray-200">
          <span className="text-sm font-medium text-gray-700 flex-1 break-all">
            {documentUrl.split('/').pop() || ''}
          </span>
          <button
            type="button"
            onClick={onClear}
            className="text-gray-500 hover:text-gray-700 focus:outline-none flex-shrink-0"
            aria-label="Remove document"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {documentUrl.toLowerCase().endsWith('.pdf') ? (
          <iframe
            src={documentPreviewUrl ?? documentUrl}
            className="w-full h-[400px] border border-gray-200 rounded-md"
            title="PDF Viewer"
          />
        ) : (
          <ImageViewer src={documentPreviewUrl ?? documentUrl} alt="Document preview" />
        )}
      </div>
    )

  return (
    <Upload
      title={title}
      uploadContext={uploadContext}
      onStarted={onStarted}
      onComplete={onComplete}
    />
  )
}

export default DocumentUpload
